import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, LogOut, ChevronLeft, ChevronRight, X, Trash2, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { API } from '../App';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday, getDay, addDays, subDays } from 'date-fns';

const LOGO = "https://customer-assets.emergentagent.com/job_logan-salon/artifacts/cyvqt31h_IMG_6012.jpg";

// Time slots for blocking
const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
  "18:00", "18:30", "19:00", "19:30"
];

export default function DashboardPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [blockDate, setBlockDate] = useState(new Date()); // Separate date for Block Times section
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  // Active section for mobile tabs
  const [activeSection, setActiveSection] = useState('calendar');

  useEffect(() => {
    const auth = sessionStorage.getItem('dashboardAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchStats();
      fetchBlockedDates();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/dashboard/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('dashboardAuth', 'true');
        toast.success('Welcome!');
      }
    } catch (error) {
      setLoginError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('dashboardAuth');
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await axios.get(`${API}/blocked-dates`);
      setBlockedDates(response.data);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  // Block entire day
  const blockDay = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      await axios.post(`${API}/blocked-dates`, { date: dateStr, time: null });
      toast.success('Day blocked');
      fetchBlockedDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Already blocked');
    }
  };

  // Block specific time
  const blockTime = async (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      await axios.post(`${API}/blocked-dates`, { date: dateStr, time: time });
      toast.success(`${time} blocked`);
      fetchBlockedDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Already blocked');
    }
  };

  // Unblock
  const handleUnblock = async (date, time = null) => {
    try {
      const url = time 
        ? `${API}/blocked-dates/${date}?time=${encodeURIComponent(time)}`
        : `${API}/blocked-dates/${date}`;
      await axios.delete(url);
      toast.success('Unblocked');
      fetchBlockedDates();
    } catch (error) {
      toast.error('Failed to unblock');
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}`);
      fetchBookings();
      fetchStats();
      setShowBookingModal(false);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success('Deleted');
      fetchBookings();
      fetchStats();
      setShowBookingModal(false);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getBookingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr && b.status !== 'cancelled');
  };

  const isWholeDayBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.some(b => b.date === dateStr && !b.time);
  };

  const getBlockedTimesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.filter(b => b.date === dateStr && b.time).map(b => b.time);
  };

  const isTimeBooked = (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.some(b => b.date === dateStr && b.time === time && b.status !== 'cancelled');
  };

  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const startDayOfWeek = getDay(startOfMonth(currentMonth));

  const isWorkingDay = (date) => {
    const day = getDay(date);
    return day === 3 || day === 4 || day === 5; // Wed, Thu, Fri
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="w-full max-w-xs">
          <div className="text-center mb-8">
            <img src={LOGO} alt="Logan Collective" className="w-16 h-16 rounded-full mx-auto mb-4 border-2 border-bronze/30" />
            <h1 className="font-heading text-2xl text-cream">Logan's Dashboard</h1>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-transparent border border-cream/20 text-cream px-4 py-3 text-center focus:border-bronze outline-none"
              data-testid="login-password"
            />
            {loginError && <p className="text-red-400 text-sm text-center">{loginError}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-bronze text-cream py-3 text-sm uppercase tracking-wider font-semibold hover:bg-bronze/80"
              data-testid="login-submit"
            >
              {loading ? '...' : 'Enter'}
            </button>
          </form>

          <Link to="/" className="block text-center mt-6 text-cream/40 text-sm hover:text-cream">
            ← Back to Website
          </Link>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-[#F8F5F0]">
      {/* Header */}
      <header className="bg-[#1A1A1A] px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="LC" className="w-10 h-10 rounded-full border border-bronze/30" />
          <div>
            <p className="text-cream/50 text-[10px] uppercase tracking-wider">Welcome back</p>
            <span className="font-heading text-cream text-lg">Logan</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-cream/50 hover:text-cream"><Home size={20} /></Link>
          <button onClick={handleLogout} className="text-cream/50 hover:text-cream"><LogOut size={20} /></button>
        </div>
      </header>

      {/* Mobile Tabs */}
      <div className="md:hidden flex border-b border-[#D6CFC2] bg-white sticky top-[68px] z-10">
        <button 
          onClick={() => setActiveSection('calendar')}
          className={`flex-1 py-4 text-xs uppercase tracking-wider font-semibold ${activeSection === 'calendar' ? 'text-bronze border-b-2 border-bronze bg-bronze/5' : 'text-[#1A1A1A]/40'}`}
        >
          Calendar
        </button>
        <button 
          onClick={() => setActiveSection('block')}
          className={`flex-1 py-4 text-xs uppercase tracking-wider font-semibold ${activeSection === 'block' ? 'text-bronze border-b-2 border-bronze bg-bronze/5' : 'text-[#1A1A1A]/40'}`}
        >
          Block Times
        </button>
        <button 
          onClick={() => setActiveSection('bookings')}
          className={`flex-1 py-4 text-xs uppercase tracking-wider font-semibold ${activeSection === 'bookings' ? 'text-bronze border-b-2 border-bronze bg-bronze/5' : 'text-[#1A1A1A]/40'}`}
        >
          Bookings
        </button>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="md:grid md:grid-cols-3 md:gap-10">
          
          {/* SECTION 1: Calendar */}
          <div className={`${activeSection !== 'calendar' ? 'hidden md:block' : ''} md:col-span-1`}>
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8 mb-4 md:mb-0 shadow-sm">
              <h2 className="text-sm uppercase tracking-[0.2em] text-bronze font-semibold mb-5 flex items-center gap-2">
                <span className="w-2 h-2 bg-bronze rounded-full"></span>
                Calendar
              </h2>
              
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-5">
                <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-[#F5F0E8] rounded-lg">
                  <ChevronLeft size={22} className="text-[#1A1A1A]/60" />
                </button>
                <span className="font-heading text-xl">{format(currentMonth, 'MMMM yyyy')}</span>
                <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-[#F5F0E8] rounded-lg">
                  <ChevronRight size={22} className="text-[#1A1A1A]/60" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2 md:gap-3">
                {dayNames.map((day, i) => (
                  <div key={i} className="text-center py-2 text-sm font-semibold text-[#1A1A1A]/40">{day}</div>
                ))}
                {[...Array(startDayOfWeek)].map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                {calendarDays.map(day => {
                  const dayBookings = getBookingsForDate(day);
                  const isSelected = isSameDay(day, selectedDate);
                  const isWorking = isWorkingDay(day);
                  const wholeDayBlocked = isWholeDayBlocked(day);

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={`aspect-square flex flex-col items-center justify-center text-base md:text-lg rounded-xl transition-all ${
                        wholeDayBlocked
                          ? 'bg-red-100 text-red-400'
                          : isSelected 
                            ? 'bg-bronze text-cream shadow-md' 
                            : isToday(day)
                              ? 'bg-bronze/20 font-semibold'
                              : isWorking
                                ? 'bg-[#F5F0E8] hover:bg-bronze/10'
                                : 'text-[#1A1A1A]/20'
                      }`}
                    >
                      <span className={isSelected ? 'font-bold' : ''}>{format(day, 'd')}</span>
                      {dayBookings.length > 0 && !wholeDayBlocked && (
                        <span className={`w-2 h-2 rounded-full mt-0.5 ${isSelected ? 'bg-cream' : 'bg-bronze'}`}></span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Selected Date Info + Bookings */}
              <div className="mt-6 pt-5 border-t border-[#E5E0D8]">
                <p className="font-heading text-lg md:text-xl">{format(selectedDate, 'EEEE, d MMMM')}</p>
                {!isWorkingDay(selectedDate) ? (
                  <p className="text-sm text-[#1A1A1A]/40 mt-2">Shop closed</p>
                ) : isWholeDayBlocked(selectedDate) ? (
                  <p className="text-sm text-red-500 mt-2">Day is blocked</p>
                ) : (
                  <>
                    <p className="text-sm text-[#1A1A1A]/40 mt-2 mb-4">{getBookingsForDate(selectedDate).length} booking(s)</p>
                    {/* Bookings for selected date */}
                    {getBookingsForDate(selectedDate).length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {getBookingsForDate(selectedDate)
                          .sort((a, b) => a.time.localeCompare(b.time))
                          .map(booking => (
                            <button
                              key={booking.id}
                              onClick={() => { setSelectedBooking(booking); setShowBookingModal(true); }}
                              className="w-full text-left p-3 bg-[#F5F0E8] rounded-lg hover:bg-bronze/10 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-heading text-sm text-bronze">{booking.time}</span>
                                <span className="text-xs text-[#1A1A1A]/50">${booking.final_price}</span>
                              </div>
                              <p className="text-sm font-medium">{booking.client_name}</p>
                              <p className="text-[10px] text-[#1A1A1A]/40">{booking.service_name}</p>
                            </button>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: Block Times */}
          <div className={`${activeSection !== 'block' ? 'hidden md:block' : ''} md:col-span-1`}>
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8 mb-4 md:mb-0 shadow-sm">
              <h2 className="text-sm uppercase tracking-[0.2em] text-bronze font-semibold mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-bronze rounded-full"></span>
                Block Times
              </h2>
              
              {/* Date Selector for blocking */}
              <div className="flex items-center justify-between mb-5 bg-[#F5F0E8] rounded-xl p-3">
                <button 
                  onClick={() => setBlockDate(subDays(blockDate, 1))}
                  className="p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} className="text-[#1A1A1A]/60" />
                </button>
                <div className="text-center">
                  <p className="font-heading text-base">{format(blockDate, 'EEEE')}</p>
                  <p className="text-sm text-[#1A1A1A]/50">{format(blockDate, 'd MMMM yyyy')}</p>
                </div>
                <button 
                  onClick={() => setBlockDate(addDays(blockDate, 1))}
                  className="p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <ChevronRight size={20} className="text-[#1A1A1A]/60" />
                </button>
              </div>

              {!isWorkingDay(blockDate) ? (
                <p className="text-base text-[#1A1A1A]/40 text-center py-12">Shop closed this day</p>
              ) : (
                <>
                  {/* Block Entire Day Button */}
                  {!isWholeDayBlocked(blockDate) ? (
                    <button 
                      onClick={() => blockDay(blockDate)}
                      className="w-full py-4 mb-5 border-2 border-dashed border-red-300 text-red-500 rounded-xl hover:bg-red-50 transition-colors font-medium"
                    >
                      Block Entire Day
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleUnblock(format(blockDate, 'yyyy-MM-dd'))}
                      className="w-full py-4 mb-5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium"
                    >
                      Unblock Day
                    </button>
                  )}

                  {/* Time Slots Grid */}
                  {!isWholeDayBlocked(blockDate) && (
                    <>
                      <p className="text-xs text-[#1A1A1A]/40 uppercase tracking-wider mb-3 font-medium">Or block specific times:</p>
                      <div className="grid grid-cols-4 gap-2 md:gap-3">
                        {TIME_SLOTS.map(time => {
                          const isBlocked = getBlockedTimesForDate(blockDate).includes(time);
                          const isBooked = isTimeBooked(blockDate, time);
                          
                          return (
                            <button
                              key={time}
                              onClick={() => isBlocked ? handleUnblock(format(blockDate, 'yyyy-MM-dd'), time) : blockTime(blockDate, time)}
                              disabled={isBooked}
                              className={`py-4 text-sm rounded-xl transition-all font-medium ${
                                isBooked
                                  ? 'bg-bronze/20 text-bronze cursor-not-allowed'
                                  : isBlocked
                                    ? 'bg-red-500 text-white shadow-sm'
                                    : 'bg-[#F5F0E8] text-[#1A1A1A]/60 hover:bg-red-100 hover:text-red-500'
                              }`}
                              title={isBooked ? 'Has booking' : isBlocked ? 'Click to unblock' : 'Click to block'}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-6 mt-6 pt-5 border-t border-[#E5E0D8] text-sm text-[#1A1A1A]/50">
                    <span className="flex items-center gap-2"><span className="w-4 h-4 bg-[#F5F0E8] rounded"></span> Available</span>
                    <span className="flex items-center gap-2"><span className="w-4 h-4 bg-red-500 rounded"></span> Blocked</span>
                    <span className="flex items-center gap-2"><span className="w-4 h-4 bg-bronze/20 rounded"></span> Booked</span>
                  </div>
                </>
              )}

              {/* Currently Blocked List */}
              {blockedDates.length > 0 && (
                <div className="mt-6 pt-5 border-t border-[#E5E0D8]">
                  <p className="text-xs text-[#1A1A1A]/40 uppercase tracking-wider mb-3 font-medium">All Blocked</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {blockedDates.slice(0, 15).map(b => (
                      <div key={b.id} className="flex items-center justify-between py-3 px-4 bg-red-50 rounded-xl text-sm">
                        <span className="text-red-600 font-medium">
                          {format(new Date(b.date), 'EEE, d MMM')} {b.time && `@ ${b.time}`}
                        </span>
                        <button onClick={() => handleUnblock(b.date, b.time)} className="text-red-400 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 3: Bookings */}
          <div className={`${activeSection !== 'bookings' ? 'hidden md:block' : ''} md:col-span-1`}>
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-sm uppercase tracking-[0.2em] text-bronze font-semibold mb-5 flex items-center gap-2">
                <span className="w-2 h-2 bg-bronze rounded-full"></span>
                Bookings
              </h2>

              {/* Today's Bookings */}
              <div className="mb-6">
                <p className="text-xs text-[#1A1A1A]/40 uppercase tracking-wider mb-4 font-medium">
                  {format(selectedDate, 'EEEE, d MMM')}
                </p>
                {getBookingsForDate(selectedDate).length === 0 ? (
                  <p className="text-base text-[#1A1A1A]/30 py-8 text-center">No bookings</p>
                ) : (
                  <div className="space-y-3">
                    {getBookingsForDate(selectedDate)
                      .sort((a, b) => a.time.localeCompare(b.time))
                      .map(booking => (
                        <button
                          key={booking.id}
                          onClick={() => { setSelectedBooking(booking); setShowBookingModal(true); }}
                          className="w-full text-left p-5 bg-[#F5F0E8] rounded-xl hover:bg-bronze/10 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-heading text-lg text-bronze">{booking.time}</span>
                            <span className="text-base text-[#1A1A1A]/50">${booking.final_price}</span>
                          </div>
                          <p className="text-base font-semibold mt-2">{booking.client_name}</p>
                          <p className="text-sm text-[#1A1A1A]/40 mt-1">{booking.service_name}</p>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* All Upcoming */}
              <div className="border-t border-[#E5E0D8] pt-6">
                <p className="text-xs text-[#1A1A1A]/40 uppercase tracking-wider mb-4 font-medium">All Upcoming</p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {bookings
                    .filter(b => b.status !== 'cancelled')
                    .slice(0, 15)
                    .map(booking => (
                      <button
                        key={booking.id}
                        onClick={() => { setSelectedBooking(booking); setShowBookingModal(true); }}
                        className="w-full text-left px-5 py-4 bg-[#F5F0E8] rounded-xl hover:bg-bronze/10 transition-colors flex items-center justify-between"
                      >
                        <div>
                          <p className="text-base font-medium">{booking.client_name}</p>
                          <p className="text-sm text-[#1A1A1A]/40">{format(new Date(booking.date), 'd MMM')} • {booking.time}</p>
                        </div>
                        <span className="text-base text-bronze font-semibold">${booking.final_price}</span>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Booking Detail Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white max-w-sm mx-auto p-6 rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Booking Details</DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-[#F5F0E8] rounded-lg p-4">
                <p className="font-heading text-bronze text-lg">{selectedBooking.time}</p>
                <p className="text-sm font-medium mt-1">{selectedBooking.client_name}</p>
                <p className="text-xs text-[#1A1A1A]/50">{format(new Date(selectedBooking.date), 'EEEE, d MMMM yyyy')}</p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/50">Service</span>
                  <span>{selectedBooking.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/50">Phone</span>
                  <a href={`tel:${selectedBooking.client_phone}`} className="text-bronze">{selectedBooking.client_phone}</a>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/50">Price</span>
                  <span className="font-heading text-bronze">${selectedBooking.final_price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/50">Status</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    selectedBooking.status === 'confirmed' ? 'bg-bronze/10 text-bronze' :
                    selectedBooking.status === 'completed' ? 'bg-green-100 text-green-600' :
                    'bg-red-100 text-red-500'
                  }`}>{selectedBooking.status}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button 
                  onClick={() => updateBookingStatus(selectedBooking.id, 'completed')}
                  className="flex-1 py-2 bg-green-500 text-white text-xs uppercase tracking-wider rounded"
                  disabled={selectedBooking.status === 'completed'}
                >
                  Complete
                </button>
                <button 
                  onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                  className="flex-1 py-2 border border-red-300 text-red-500 text-xs uppercase tracking-wider rounded"
                  disabled={selectedBooking.status === 'cancelled'}
                >
                  Cancel
                </button>
                <button 
                  onClick={() => deleteBooking(selectedBooking.id)}
                  className="py-2 px-3 border border-red-300 text-red-500 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
